<template>
  <router-view />
  <AppNotifications />
</template>

<script>
import AppNotifications from '@/components/AppNotifications.vue'

export default {
  name: 'App',
  components: { AppNotifications },
}
</script>

<style>
/* ─── Variables ─── */
:root {
  --c-primary:  #003b4c;
  --c-coral:    #ed8f7b;
  --c-peach:    #f0c2b2;
  --c-cream:    #f6dfa4;
  --c-bg:       #faf9f7;
  --c-surface:  #ffffff;
  --c-border:   #e8e4df;
  --c-border-s: #d4cfc9;
  --c-text:     #1a1a1a;
  --c-text2:    #4a4a4a;
  --c-text3:    #7a7570;
  --c-overlay:  rgba(0,59,76,0.06);
  --f-body: marine, 'Inter', 'Poppins', system-ui, sans-serif;
  --f-serif: 'Lora', Georgia, serif;
  --nav-h: 72px;
  --nav-h-sm: 52px;
  --r-sm:  8px;
  --r-md: 14px;
  --r-lg: 20px;
  --r-xl: 28px;
  --sh-xs: 0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04);
  --sh-sm: 0 2px 8px rgba(0,0,0,0.07), 0 1px 3px rgba(0,0,0,0.04);
  --sh-md: 0 4px 16px rgba(0,0,0,0.08), 0 2px 6px rgba(0,0,0,0.04);
  --sh-lg: 0 8px 32px rgba(0,0,0,0.10), 0 3px 10px rgba(0,0,0,0.04);
  --success-color: #28a745;
  --error-color: #dc3545;
}

*, *::before, *::after { box-sizing: border-box; }

html, body, #app {
  scroll-behavior: smooth;
  scrollbar-width: none;
  -ms-overflow-style: none;
}
html::-webkit-scrollbar,
body::-webkit-scrollbar,
#app::-webkit-scrollbar { display: none; }

body {
  font-family: var(--f-body);
  margin: 0;
  padding: 0;
  color: var(--c-text);
  background: var(--c-bg);
  -webkit-font-smoothing: antialiased;
}

.container {
  max-width: 1120px;
  margin: 0 auto;
  padding: 0 24px;
}

/* ─── Label chip ─── */
.label-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--c-text3);
  background: var(--c-overlay);
  border: 1px solid var(--c-border);
  padding: 5px 14px;
  border-radius: 999px;
  margin-bottom: 14px;
}
.label-chip i { color: var(--c-coral); }

.section-title {
  font-family: var(--f-serif);
  font-size: clamp(1.75rem, 3vw, 2.5rem);
  font-weight: 600;
  color: var(--c-primary);
  line-height: 1.2;
  margin: 0 0 10px;
}

.section-desc {
  font-size: 1rem;
  color: var(--c-text3);
  margin: 0 0 40px;
  max-width: 500px;
}

/* ─── Buttons ─── */
.btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 11px 26px;
  border-radius: 999px;
  font-family: var(--f-body);
  font-size: 0.88rem;
  font-weight: 600;
  cursor: pointer;
  border: 1.5px solid transparent;
  text-decoration: none;
  transition: all 0.18s ease;
  white-space: nowrap;
}
.btn-sm { padding: 8px 18px; font-size: 0.8rem; }
.btn-block { width: 100%; justify-content: center; }

.btn-primary {
  background: var(--c-primary);
  color: #fff;
  border-color: var(--c-primary);
}
.btn-primary:hover {
  background: #004f67;
  border-color: #004f67;
  box-shadow: 0 4px 14px rgba(0,59,76,0.28);
  transform: translateY(-1px);
}

.btn-ghost {
  background: transparent;
  color: var(--c-text2);
  border-color: var(--c-border-s);
}
.btn-ghost:hover {
  background: var(--c-overlay);
  border-color: var(--c-primary);
  color: var(--c-primary);
}

/* ─── Navbar ─── */
.navbar {
  position: fixed;
  top: 0; left: 0; right: 0;
  z-index: 1000;
  height: var(--nav-h);
  background: rgba(255,255,255,0.88);
  backdrop-filter: blur(16px) saturate(1.4);
  -webkit-backdrop-filter: blur(16px) saturate(1.4);
  border-bottom: 1px solid var(--c-border);
  display: flex;
  align-items: center;
  transition: height 0.28s ease, box-shadow 0.28s ease, background 0.28s ease;
}
.navbar.scrolled {
  height: var(--nav-h-sm);
  background: rgba(255,255,255,0.96);
  box-shadow: var(--sh-sm);
}
.nav-inner {
  width: 100%;
  max-width: 1120px;
  margin: 0 auto;
  padding: 0 24px;
  display: flex;
  align-items: center;
  gap: 24px;
}
.nav-logo img {
  height: 64px;
  object-fit: contain;
  transition: height 0.28s ease;
}
.navbar.scrolled .nav-logo img { height: 48px; }
.nav-links {
  display: flex;
  gap: 4px;
  list-style: none;
  margin: 0;
  padding: 0;
  flex: 1;
}
.nav-links a {
  font-family: var(--f-body);
  color: var(--c-text2);
  text-decoration: none;
  font-size: 0.85rem;
  font-weight: 500;
  padding: 7px 14px;
  border-radius: 999px;
  transition: color 0.15s, background 0.15s;
}
.nav-links a:hover {
  color: var(--c-primary);
  background: var(--c-overlay);
}
.nav-cta-pay {
  margin-left: auto;
  background: transparent;
  border: 1.5px solid var(--c-primary);
  color: var(--c-primary);
  font-family: marine, 'Inter', sans-serif;
}
.nav-cta-pay:hover { background: var(--c-primary); color: #fff; }
.nav-cta { margin-left: 0.5rem; }
.nav-toggle {
  display: none;
  flex-direction: column;
  gap: 5px;
  cursor: pointer;
  padding: 6px;
  background: none;
  border: none;
  margin-left: auto;
}
.nav-toggle span {
  display: block;
  width: 22px; height: 2px;
  background: var(--c-text2);
  border-radius: 2px;
  transition: all 0.28s;
}
.nav-toggle.open span:nth-child(1) { transform: translateY(7px) rotate(45deg); }
.nav-toggle.open span:nth-child(2) { opacity: 0; }
.nav-toggle.open span:nth-child(3) { transform: translateY(-7px) rotate(-45deg); }

/* ─── Hero ─── */
.hero-section {
  min-height: 100vh;
  display: flex;
  align-items: center;
  padding-top: var(--nav-h);
  background: #ffffff;
  position: relative;
  overflow: hidden;
}
.hero-particles {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 0;
}
.hero-section::before {
  content: '';
  position: absolute;
  top: 5%; right: -5%;
  width: 55vw; height: 55vw;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(240,194,178,0.5) 0%, transparent 70%);
  pointer-events: none;
}
.hero-section::after {
  content: '';
  position: absolute;
  bottom: 5%; left: -10%;
  width: 40vw; height: 40vw;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(255,255,255,0.5) 0%, transparent 70%);
  pointer-events: none;
}
.hero-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 48px;
  align-items: center;
  position: relative;
  z-index: 1;
  padding-top: 40px;
  padding-bottom: 40px;
}
.hero-title {
  font-family: var(--f-serif);
  font-size: clamp(2.6rem, 5.5vw, 4.2rem);
  font-weight: 600;
  color: var(--c-primary);
  line-height: 1.1;
  margin: 0 0 20px;
}
.hero-title em { font-family: 'Dancing Script', cursive; font-style: normal; font-weight: 700; color: var(--c-coral); }
.hero-desc {
  font-size: 1.08rem;
  color: var(--c-text3);
  max-width: 420px;
  line-height: 1.7;
  margin: 0 0 32px;
}
.hero-actions { display: flex; gap: 12px; flex-wrap: wrap; }

.hero-visual { display: flex; justify-content: center; align-items: center; }
.hero-card {
  background: var(--c-surface);
  border: 1px solid var(--c-border);
  border-radius: var(--r-xl);
  padding: 40px 32px;
  box-shadow: var(--sh-lg);
  text-align: center;
  max-width: 340px;
  width: 100%;
  position: relative;
}
.hero-card::before {
  content: '';
  position: absolute;
  top: -16px; right: -16px;
  width: 48px; height: 48px;
  background: var(--c-cream);
  border-radius: 50%;
  border: 4px solid var(--c-bg);
}
.hero-card::after {
  content: '';
  position: absolute;
  bottom: -10px; left: -10px;
  width: 28px; height: 28px;
  background: var(--c-peach);
  border-radius: 50%;
  border: 3px solid var(--c-bg);
}
.hero-card-logo { width: 160px; object-fit: contain; margin-bottom: 12px; }
.hero-card-tagline { font-size: 0.85rem; color: var(--c-text3); margin: 0 0 24px; }
.hero-stats {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
}
.hero-stat {
  background: var(--c-bg);
  border: 1px solid var(--c-border);
  border-radius: var(--r-md);
  padding: 12px 8px;
}
.hero-stat strong { display: block; font-size: 1.3rem; color: var(--c-primary); font-weight: 700; }
.hero-stat span { font-size: 0.68rem; color: var(--c-text3); text-transform: uppercase; letter-spacing: 0.06em; }

/* ─── Novedades ─── */
.novedades-section {
  background: #f0c2b2;
  padding: 96px 0;
}
.product-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(230px, 1fr));
  gap: 20px;
}
.product-card {
  background: var(--c-bg);
  border: 1px solid var(--c-border);
  border-radius: var(--r-lg);
  overflow: hidden;
  transition: transform 0.2s, box-shadow 0.2s, border-color 0.2s;
}
.product-card:hover {
  transform: translateY(-4px);
  box-shadow: var(--sh-md);
  border-color: var(--c-border-s);
}
.product-thumb {
  height: 180px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 3rem;
  color: var(--c-primary);
  opacity: 0.7;
}
.product-body { padding: 20px; }
.product-badge {
  display: inline-block;
  background: var(--c-coral);
  color: #fff;
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  padding: 2px 9px;
  border-radius: 999px;
  margin-bottom: 8px;
}
.product-body h3 {
  font-family: var(--f-body);
  font-size: 0.92rem;
  font-weight: 600;
  color: var(--c-text);
  margin: 0 0 6px;
  line-height: 1.35;
}
.product-body .price {
  font-size: 1.1rem;
  font-weight: 700;
  color: var(--c-primary);
  margin: 0 0 14px;
}
.product-body .old-price {
  font-size: 0.78rem;
  color: var(--c-text3);
  font-weight: 400;
  text-decoration: line-through;
  margin-left: 6px;
}

/* ─── Categorías ─── */
.categorias-section {
  background: #faf9f7;
  padding: 96px 0;
}
.category-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
}
.category-card {
  border-radius: var(--r-lg);
  overflow: hidden;
  border: 1px solid var(--c-border);
  background: var(--c-surface);
  cursor: pointer;
  transition: transform 0.22s, box-shadow 0.22s, border-color 0.22s;
  text-decoration: none;
  color: inherit;
}
.category-card:hover {
  transform: translateY(-5px);
  box-shadow: var(--sh-md);
  border-color: var(--c-border-s);
}
.cat-visual {
  height: 160px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2.8rem;
  color: var(--c-primary);
  opacity: 0.7;
  transition: transform 0.3s;
}
.category-card:hover .cat-visual { transform: scale(1.05); }
.cat-body { padding: 14px 18px; }
.cat-name { font-size: 0.9rem; font-weight: 700; color: var(--c-primary); margin-bottom: 2px; }
.cat-sub  { font-size: 0.76rem; color: var(--c-text3); }

/* ─── Taller Creativo ─── */
.taller-section {
  background: #f0c2b2;
  padding: 96px 0;
}
.taller-carousel {
  position: relative;
  display: flex;
  align-items: stretch;
  gap: 12px;
}
.taller-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  flex: 1;
  min-width: 0;
  align-items: stretch;
  position: relative;
}
.taller-nav {
  flex-shrink: 0;
  align-self: center;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  border: 1px solid var(--c-border-s);
  background: #fff;
  color: var(--c-primary);
  font-size: 0.95rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: var(--sh-sm);
  transition: background 0.18s, transform 0.18s, color 0.18s;
}
.taller-nav:hover {
  background: var(--c-primary);
  color: #fff;
  transform: scale(1.08);
}
.taller-card {
  background: #ffffff;
  border: 1px solid var(--c-border);
  border-radius: var(--r-lg);
  padding: 0;
  overflow: hidden;
  position: relative;
  transition: transform 0.25s, box-shadow 0.25s, border-color 0.25s, opacity 0.25s;
  opacity: 0.78;
}
.taller-card:hover {
  transform: translateY(-4px);
  box-shadow: var(--sh-md);
  border-color: var(--c-border-s);
}
.taller-card--center {
  opacity: 1;
  box-shadow: var(--sh-lg);
  border-color: var(--c-primary);
  z-index: 1;
}

.taller-dots {
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-top: 24px;
}
.taller-dot {
  width: 9px;
  height: 9px;
  border-radius: 50%;
  border: none;
  background: var(--c-border-s);
  cursor: pointer;
  padding: 0;
  transition: background 0.18s, transform 0.18s;
}
.taller-dot:hover { background: var(--c-text3); }
.taller-dot--active {
  background: var(--c-primary);
  transform: scale(1.3);
}

@keyframes taller-pulse-glow {
  0%   { box-shadow: 0 0 0 0 rgba(0, 59, 76, 0.45); }
  100% { box-shadow: 0 0 0 22px rgba(0, 59, 76, 0); }
}
.taller-nav.is-pulsing { animation: taller-pulse-glow 0.6s ease-out; }

@keyframes taller-dot-pulse-glow {
  0%   { box-shadow: 0 0 0 0 rgba(237, 143, 123, 0.55); }
  100% { box-shadow: 0 0 0 10px rgba(237, 143, 123, 0); }
}
.taller-dot.is-pulsing { animation: taller-dot-pulse-glow 0.55s ease-out; }

/* ─── Carrusel: slide suave al cambiar de slide ─── */
.taller-slide-next-move,
.taller-slide-prev-move,
.taller-slide-next-enter-active,
.taller-slide-prev-enter-active,
.taller-slide-next-leave-active,
.taller-slide-prev-leave-active {
  transition: transform 0.5s cubic-bezier(0.4, 0, 0.2, 1),
              opacity 0.4s ease-out,
              box-shadow 0.4s ease,
              border-color 0.4s ease;
}
/* Next: entra desde la derecha, sale por la izquierda */
.taller-slide-next-enter-from { opacity: 0; transform: translateX(60px); }
.taller-slide-next-leave-to   { opacity: 0; transform: translateX(-60px); }
/* Prev: entra desde la izquierda, sale por la derecha */
.taller-slide-prev-enter-from { opacity: 0; transform: translateX(-60px); }
.taller-slide-prev-leave-to   { opacity: 0; transform: translateX(60px); }
/* La card que sale se posiciona absolute para que las que quedan puedan deslizarse a su lugar */
.taller-slide-next-leave-active,
.taller-slide-prev-leave-active {
  position: absolute;
  width: calc((100% - 40px) / 3);
}

/* ─── Banner ─── */
.taller-card-banner {
  height: 130px;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}
.taller-card-banner--peach { background: linear-gradient(135deg, #f0c2b2 0%, #f6dfa4 100%); }
.taller-card-banner--teal  { background: linear-gradient(135deg, #003b4c 0%, #005f78 100%); }
.taller-card-banner--coral { background: linear-gradient(135deg, #ed8f7b 0%, #f0c2b2 100%); }

.taller-card-banner-icon {
  font-size: 4rem;
  position: relative;
  z-index: 1;
}
.taller-card-banner--peach .taller-card-banner-icon,
.taller-card-banner--coral .taller-card-banner-icon { color: rgba(0,59,76,0.22); }
.taller-card-banner--teal  .taller-card-banner-icon { color: rgba(255,255,255,0.28); }

.taller-card-deco-a,
.taller-card-deco-b {
  position: absolute;
  border-radius: 50%;
  pointer-events: none;
}
.taller-card-deco-a { width: 90px; height: 90px; top: -25px; right: -25px; background: rgba(255,255,255,0.15); }
.taller-card-deco-b { width: 55px; height: 55px; bottom: -18px; left: 18px; background: rgba(255,255,255,0.10); }

.taller-card-content { padding: 24px 28px 28px; }

.taller-card-badge {
  display: inline-block;
  background: var(--c-cream);
  color: var(--c-primary);
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  padding: 3px 10px;
  border-radius: 999px;
  margin-bottom: 12px;
}
.taller-card-badge--new {
  background: var(--c-coral);
  color: #fff;
}
.taller-card h3 {
  font-family: var(--f-body);
  font-size: 1rem;
  font-weight: 700;
  color: var(--c-primary);
  margin: 0 0 8px;
}
.taller-card p {
  font-size: 0.88rem;
  color: var(--c-text3);
  line-height: 1.6;
  margin: 0;
}

/* ─── Contacto ─── */
.contacto-section {
  background: #faf9f7;
  padding: 96px 0;
}
.contact-grid {
  display: grid;
  grid-template-columns: 1fr 1.35fr;
  gap: 48px;
  align-items: start;
}
.contact-side h3 {
  font-family: var(--f-serif);
  font-size: 1.25rem;
  color: var(--c-primary);
  margin: 0 0 24px;
}
.contact-item {
  display: flex;
  gap: 14px;
  align-items: flex-start;
  margin-bottom: 20px;
}
/* Los items accionables son <a>: toda la tarjeta es el área de clic.
   El padding negativo conserva la alineación y el ritmo vertical originales. */
a.contact-item {
  text-decoration: none;
  color: inherit;
  cursor: pointer;
  padding: 8px;
  margin: -8px -8px 12px;
  border-radius: var(--r-md);
  transition: background 0.18s;
}
a.contact-item:hover { background: var(--c-overlay); }
a.contact-item:hover .contact-icon {
  background: var(--c-primary);
  border-color: var(--c-primary);
  color: #fff;
}
a.contact-item:hover .contact-text span { color: var(--c-coral); }
a.contact-item:focus-visible {
  outline: 2px solid var(--c-primary);
  outline-offset: 2px;
}
.contact-icon {
  width: 40px; height: 40px;
  border-radius: var(--r-md);
  background: var(--c-overlay);
  border: 1px solid var(--c-border);
  color: var(--c-primary);
  display: flex; align-items: center; justify-content: center;
  font-size: 0.9rem;
  flex-shrink: 0;
  transition: background 0.18s, border-color 0.18s, color 0.18s;
}
.contact-text strong {
  display: block;
  font-size: 0.76rem;
  font-weight: 700;
  color: var(--c-text);
  text-transform: uppercase;
  letter-spacing: 0.06em;
  margin-bottom: 2px;
}
.contact-text span {
  font-size: 0.9rem;
  color: var(--c-text3);
  text-decoration: none;
  transition: color 0.15s;
}

/* Form card */
.form-card {
  background: #ffffff;
  border: 1px solid var(--c-border);
  border-radius: var(--r-xl);
  padding: 40px;
  box-shadow: var(--sh-xs);
}
.form-card h4 {
  font-size: 1rem;
  font-weight: 700;
  color: var(--c-primary);
  margin: 0 0 24px;
}
.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
}
.form-group { margin-bottom: 16px; }
.form-group label {
  display: block;
  font-size: 0.76rem;
  font-weight: 600;
  color: var(--c-text2);
  text-transform: uppercase;
  letter-spacing: 0.07em;
  margin-bottom: 6px;
}
.form-group input,
.form-group textarea {
  width: 100%;
  padding: 10px 14px;
  border: 1.5px solid var(--c-border);
  border-radius: var(--r-sm);
  font-family: var(--f-body);
  font-size: 0.9rem;
  color: var(--c-text);
  background: var(--c-surface);
  outline: none;
  transition: border-color 0.18s, box-shadow 0.18s;
  box-sizing: border-box;
}
.form-group input:focus,
.form-group textarea:focus {
  border-color: var(--c-primary);
  box-shadow: 0 0 0 3px rgba(0,59,76,0.08);
}
.form-group textarea { resize: vertical; min-height: 110px; }

.captcha-row {
  display: flex;
  gap: 10px;
  align-items: flex-end;
  margin-bottom: 14px;
  flex-wrap: wrap;
}
.captcha-code {
  background: var(--c-primary);
  color: var(--c-cream);
  font-family: 'Courier New', monospace;
  font-size: 1rem;
  letter-spacing: 0.28em;
  padding: 10px 16px;
  border-radius: var(--r-sm);
  user-select: none;
  white-space: nowrap;
  flex-shrink: 0;
}
.captcha-field { flex: 1; margin-bottom: 0; min-width: 120px; }
.refresh-btn {
  background: none;
  border: 1.5px solid var(--c-border-s);
  color: var(--c-text2);
  border-radius: 50%;
  width: 40px; height: 40px;
  cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  font-size: 0.9rem;
  flex-shrink: 0;
  transition: all 0.2s;
}
.refresh-btn:hover { background: var(--c-primary); border-color: var(--c-primary); color: #fff; transform: rotate(90deg); }

.form-note { font-size: 0.75rem; color: var(--c-text3); margin: 0 0 16px; }
.form-note a { color: var(--c-coral); text-decoration: none; }
.form-note a:hover { text-decoration: underline; }

.form-status {
  margin-top: 12px;
  padding: 10px 14px;
  border-radius: var(--r-sm);
  font-size: 0.88rem;
  font-weight: 600;
  text-align: center;
}
.form-status.success { background: var(--success-color); color: #fff; }
.form-status.error   { background: var(--error-color);   color: #fff; }
.form-status.info    { background: var(--c-overlay); color: var(--c-primary); }

/* ─── Footer ─── */
.footer-section {
  background: var(--c-primary);
  border-top: 1px solid rgba(255,255,255,0.06);
  padding: 64px 0 0;
  font-family: var(--f-body);
  font-size: 0.9rem;
}
.footer-grid {
  display: grid;
  grid-template-columns: 1.5fr 1fr 1fr 1.2fr;
  gap: 32px;
  padding-bottom: 48px;
}
.footer-logo { height: 40px; margin-bottom: 14px; display: block; }
.footer-brand p {
  font-size: 0.88rem;
  color: rgba(255,255,255,0.5);
  line-height: 1.65;
  margin: 0 0 20px;
}
.social-links { display: flex; gap: 10px; }
.social-links a {
  width: 36px; height: 36px;
  border-radius: var(--r-sm);
  background: rgba(255,255,255,0.08);
  border: 1px solid rgba(255,255,255,0.12);
  color: rgba(255,255,255,0.7);
  display: flex; align-items: center; justify-content: center;
  text-decoration: none;
  font-size: 0.88rem;
  transition: all 0.18s;
}
.social-links a:hover { background: var(--c-coral); border-color: var(--c-coral); color: #fff; transform: translateY(-2px); }
.footer-col h4 {
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: rgba(255,255,255,0.4);
  margin: 0 0 16px;
  display: block;
}
.footer-col ul { list-style: none; padding: 0; margin: 0; }
.footer-col ul li { margin-bottom: 10px; }
.footer-col ul li a,
.footer-col p a {
  font-size: 0.88rem;
  color: rgba(255,255,255,0.6);
  text-decoration: none;
  transition: color 0.15s;
  cursor: pointer;
}
.footer-col ul li a:hover,
.footer-col p a:hover { color: #fff; }
.footer-col p {
  font-size: 0.86rem;
  color: rgba(255,255,255,0.6);
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  gap: 8px;
}
.footer-col p i { color: var(--c-coral); font-size: 0.85rem; flex-shrink: 0; }
.footer-bottom {
  border-top: 1px solid rgba(255,255,255,0.08);
  padding: 20px 0;
  text-align: center;
}
.footer-bottom p { font-size: 0.78rem; color: rgba(255,255,255,0.3); margin: 0; }

/* ─── WhatsApp FAB ─── */
.whatsapp-float-btn {
  position: fixed;
  width: 52px; height: 52px;
  bottom: 28px; right: 28px;
  background: #25D366;
  color: #fff;
  border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  font-size: 1.5rem;
  box-shadow: 0 4px 16px rgba(37,211,102,0.45);
  z-index: 1500;
  text-decoration: none;
  transition: transform 0.2s, box-shadow 0.2s;
}
.whatsapp-float-btn:hover { transform: scale(1.1); box-shadow: 0 8px 24px rgba(37,211,102,0.55); }

/* ─── Modal ─── */
#app-root.modal-open { filter: blur(5px); transition: filter 0.3s ease; }
.modal-overlay {
  position: fixed;
  top: 0; left: 0; width: 100%; height: 100%;
  background: rgba(0,0,0,0.55);
  display: flex; align-items: center; justify-content: center;
  z-index: 2000;
  padding: 20px;
}

/* ─── Responsive ─── */
@media (max-width: 960px) {
  .hero-grid { grid-template-columns: 1fr; }
  .hero-visual { display: none; }
  .hero-desc { max-width: 100%; }
  .category-grid { grid-template-columns: repeat(2, 1fr); }
  .taller-grid { grid-template-columns: 1fr; }
  .taller-card:not(.taller-card--center) { display: none; }
  .taller-card--center { transform: none; }
  .taller-card--center:hover { transform: translateY(-4px); }
  .contact-grid { grid-template-columns: 1fr; }
  .footer-grid { grid-template-columns: 1fr 1fr; }
  .footer-brand { grid-column: 1 / -1; }
}

@media (max-width: 640px) {
  .nav-links {
    display: none;
    flex-direction: column;
    position: absolute;
    top: var(--nav-h);
    left: 0; right: 0;
    background: var(--c-surface);
    border-top: 1px solid var(--c-border);
    border-bottom: 1px solid var(--c-border);
    padding: 12px 20px 20px;
    gap: 4px;
  }
  .navbar.scrolled .nav-links { top: var(--nav-h-sm); }
  .nav-links.open { display: flex; }
  .nav-links a { padding: 10px 12px; border-radius: var(--r-sm); }
  .nav-cta, .nav-cta-pay { display: none; }
  .nav-toggle { display: flex; }
  .form-row { grid-template-columns: 1fr; }
  .footer-grid { grid-template-columns: 1fr; }
  section { padding: 64px 0; }
  .novedades-section,
  .categorias-section,
  .contacto-section { padding: 64px 0; }
}
</style>
